import { ScheduledQuery } from '@api';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownGroup } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { Icon } from '@design/components/icon/Icon';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useListScheduledQueryConversations } from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import { appPaths } from '@terminal/routes/app-paths';
import { useHistorySidebarNavigation } from '@terminal/stores/use-history-sidebar-navigation/useHistorySidebarNavigation';
import { useScheduleDrawer } from '@terminal/stores/use-schedule-drawer/useScheduleDrawer';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DeleteScheduledTaskModal } from '../scheduled-task-detail/DeleteScheduledTaskModal';
import styles from './ScheduledTaskItem.module.scss';

interface ScheduledTaskItemProps {
  task: ScheduledQuery;
}

export const ScheduledTaskItem = ({ task }: ScheduledTaskItemProps) => {
  const { t } = useTranslation(['chat']);
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: conversationsData } = useListScheduledQueryConversations(
    task.id,
    {},
    isDeleteModalOpen
  );
  const conversations = conversationsData?.conversations ?? [];

  const { selectedScheduledQueryId, setSelectedScheduledQueryId, setCategory } =
    useHistorySidebarNavigation(state => ({
      selectedScheduledQueryId: state.selectedScheduledQueryId,
      setSelectedScheduledQueryId: state.setSelectedScheduledQueryId,
      setCategory: state.setCategory,
    }));

  const openForEdit = useScheduleDrawer(state => state.openForEdit);

  const isActive = selectedScheduledQueryId === task.id;

  const handleClick = () => {
    setSelectedScheduledQueryId(task.id);
    navigate(appPaths.alfred);
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDropdownOpen(true);
  };

  const dropdownItems = [
    {
      label: (
        <span className={styles.defaultLabel}>
          <Icon iconName="Pencil" size={16} fillColor="var(--icon-default)" />
          {t('chat:scheduledTaskItem.edit')}
        </span>
      ),
      key: 'edit',
      onClick: () => openForEdit(task.id),
    },
    {
      label: (
        <span className={styles.criticalLabel}>
          <Icon iconName="Trash" size={16} fillColor="var(--icon-critical)" />
          {t('chat:scheduledTaskItem.delete')}
        </span>
      ),
      key: 'delete',
      onClick: () => setIsDeleteModalOpen(true),
    },
  ];

  return (
    <>
      <div
        className={clsx(styles.item, isActive && styles.isActive)}
        onClick={handleClick}
        onContextMenu={handleRightClick}
        data-testid={`scheduled-task-item-${task.id}`}
      >
        <span className={styles.iconWrapper}>
          <Icon iconName="Folder" size={16} />
        </span>
        <TooltipContainer
          label={<span className={styles.tooltipLabel}>{task.title}</span>}
          placement="left"
          offsetX={25}
          inPortal
        >
          <span className={styles.title}>{task.title}</span>
        </TooltipContainer>
        <DropdownMenu
          className={styles.dropdownContent}
          triggerNode={
            <span
              className={clsx(
                styles.dropdownTrigger,
                isDropdownOpen && styles.dropdownTriggerOpen
              )}
              onClick={e => e.stopPropagation()}
            >
              <Icon iconName="Ellipsis" size={16} />
            </span>
          }
          triggerProps={{
            onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
          }}
          contentProps={{
            align: 'end',
            side: 'bottom',
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          }}
          open={isDropdownOpen}
          onOpenChange={setIsDropdownOpen}
        >
          <DropdownGroup items={dropdownItems} />
        </DropdownMenu>
      </div>

      {isDeleteModalOpen && (
        <DeleteScheduledTaskModal
          task={task}
          conversations={conversations}
          onClose={() => setIsDeleteModalOpen(false)}
          onDeleted={() => {
            setIsDeleteModalOpen(false);
            if (selectedScheduledQueryId === task.id) {
              setSelectedScheduledQueryId(null);
              setCategory('all');
            }
          }}
        />
      )}
    </>
  );
};
